// ABOUTME: React Query orchestration hook for load spacing entity — combines config query, mutations, and form state.
// ABOUTME: Returns a uniform interface consumed by CountryConfigurationsEntity to keep the page component thin.

import { useCallback, useMemo, useRef } from 'react'

import { useLoadSpacingConfig } from './useLoadSpacingConfig'
import { useCreateLoadSpacing, useCreateLoadSpacingWithScope, useUpdateLoadSpacing, useDeleteLoadSpacing } from './useLoadSpacingMutations'
import { LoadSpacingConfig, LoadSpacingFormValues } from '../types/loadSpacing'
import { EntityActionResult } from '../context/EntityFormContext'
import type { PendingScopeRequest } from '../context/EntityFormContext'
import { getConfigScopeId, toPendingScopeConfig } from '../utils/pendingScope'

interface UseLoadSpacingEntityParams {
  countryId: string | undefined
  businessLine: string | undefined
  scopeId: string | undefined
  pendingScope?: PendingScopeRequest | undefined
  rootScopeId?: string | undefined
}

interface EntityActions {
  handleSave: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleReset: (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => void
  handleValuesChange: (values: LoadSpacingFormValues) => void
}

interface UseLoadSpacingEntityResult {
  config: LoadSpacingConfig | undefined
  isLoading: boolean
  error: Error | null
  isSaving: boolean
  actions: EntityActions
}

const equalsParent = (values: LoadSpacingFormValues, config: LoadSpacingConfig): boolean =>
  JSON.stringify(values) === JSON.stringify(config.parentValues)

const getLoadSpacingValues = (config: LoadSpacingConfig): LoadSpacingFormValues => ({
  allowStockpiling: config.allowStockpiling,
  timeOnJobSite: config.timeOnJobSite,
  spacing: config.spacing,
  flowRate: config.flowRate,
})

export const useLoadSpacingEntity = ({
  countryId,
  businessLine,
  scopeId,
  pendingScope,
  rootScopeId,
}: UseLoadSpacingEntityParams): UseLoadSpacingEntityResult => {
  const configScopeId = getConfigScopeId(scopeId, pendingScope, rootScopeId)
  const { data: rawConfig, isLoading, error } = useLoadSpacingConfig(countryId, businessLine, configScopeId, rootScopeId)

  const config = useMemo(
    () => toPendingScopeConfig(rawConfig, pendingScope, getLoadSpacingValues),
    [rawConfig, pendingScope],
  )

  const createMutation = useCreateLoadSpacing(countryId, businessLine)
  const createWithScopeMutation = useCreateLoadSpacingWithScope(countryId, businessLine)
  const updateMutation = useUpdateLoadSpacing(countryId, businessLine)
  const deleteMutation = useDeleteLoadSpacing(countryId, businessLine)

  const formValuesRef = useRef<LoadSpacingFormValues | null>(null)
  const configIdRef = useRef<string | undefined>(config?.id)
  const mutationsRef = useRef({ createMutation, createWithScopeMutation, updateMutation, deleteMutation })
  const configRef = useRef<LoadSpacingConfig | undefined>(config)
  const pendingScopeRef = useRef<PendingScopeRequest | undefined>(pendingScope)

  configIdRef.current = config?.id
  configRef.current = config
  pendingScopeRef.current = pendingScope
  mutationsRef.current = { createMutation, createWithScopeMutation, updateMutation, deleteMutation }

  const handleValuesChange = useCallback((values: LoadSpacingFormValues) => {
    formValuesRef.current = values
  }, [])

  const handleSave = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!formValuesRef.current || !configRef.current) return
      const values = formValuesRef.current
      const scopeToCreate = pendingScopeRef.current

      if (scopeToCreate) {
        if (equalsParent(values, configRef.current)) {
          onSuccess({})
          return
        }
        mutationsRef.current.createWithScopeMutation.mutate(
          { scope: scopeToCreate, values },
          { onSuccess: (result) => onSuccess({ wasOverriding: true, createdScopeId: result?.scopeId }), onError: (error) => onError?.(error) },
        )
        return
      }

      if (!scopeId) return

      if (scopeId !== rootScopeId && equalsParent(values, configRef.current)) {
        if (configIdRef.current) {
          mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
        } else {
          onSuccess({ wasDelete: true })
        }
        return
      }

      const isNew = !configIdRef.current
      const mutate = isNew ? mutationsRef.current.createMutation.mutate : mutationsRef.current.updateMutation.mutate
      mutate({ scopeId, values }, { onSuccess: () => onSuccess({ wasOverriding: true }), onError: (error) => onError?.(error) })
    },
    [scopeId, rootScopeId]
  )

  const handleReset = useCallback(
    (onSuccess: (result?: EntityActionResult) => void, onError?: (error?: unknown) => void) => {
      if (!scopeId) return
      mutationsRef.current.deleteMutation.mutate(scopeId, { onSuccess: () => onSuccess({ wasDelete: true }), onError: (error) => onError?.(error) })
    },
    [scopeId]
  )

  const actions = useMemo(
    () => ({ handleSave, handleReset, handleValuesChange }),
    [handleSave, handleReset, handleValuesChange]
  )

  const isSaving = createMutation.isPending || createWithScopeMutation.isPending || updateMutation.isPending || deleteMutation.isPending

  return {
    config,
    isLoading,
    error,
    isSaving,
    actions,
  }
}
